import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { FileSpreadsheet, Plus, Upload, Copy, Trash2, Pencil, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Templates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [hist, setHist] = useState({ enabled: false, hotel_id: "", audit_date: "", auditor_name: "" });
  const fileRef = useRef();

  const load = () => api.get("/templates").then((r) => setTemplates(r.data));
  useEffect(() => {
    load();
    api.get("/hotels").then((r) => setHotels(r.data));
  }, []);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/templates/import-excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setUploading(false);
    }
  };

  const createFromPreview = async () => {
    setCreating(true);
    try {
      if (hist.enabled) {
        const { data } = await api.post("/audits/import-historical", {
          name: preview.name,
          description: preview.description,
          sections: preview.sections,
          hotel_id: hist.hotel_id,
          audit_date: hist.audit_date,
          auditor_name: hist.auditor_name || "Historical Import",
        });
        toast.success(`Template + historical audit imported — score ${data.scores.overall_score}%`);
        setImportOpen(false);
        setPreview(null);
        setHist({ enabled: false, hotel_id: "", audit_date: "", auditor_name: "" });
        load();
        navigate(`/audits/${data.audit_id}/report`);
        return;
      }
      const { data } = await api.post("/templates", {
        name: preview.name,
        audit_category: "Imported",
        description: preview.description,
        sections: preview.sections,
      });
      toast.success(`Template created: ${data.sections.length} sections, ${preview.stats.questions} questions`);
      setImportOpen(false);
      setPreview(null);
      load();
      navigate(`/templates/${data.id}/edit`);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setCreating(false);
    }
  };

  const duplicate = async (id) => {
    await api.post(`/templates/${id}/duplicate`);
    toast.success("Template duplicated");
    load();
  };

  const remove = async (id) => {
    await api.delete(`/templates/${id}`);
    toast.success("Template deleted");
    load();
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-stone-900">Audit Templates</h1>
          <p className="text-stone-500 mt-1 text-sm">Build templates or import your existing Excel audit formats.</p>
        </div>
        <div className="flex gap-3">
          <Button data-testid="import-excel-button" variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="w-4 h-4 mr-2" />Import from Excel
          </Button>
          <Button data-testid="new-template-button" className="bg-[#14532D] hover:bg-[#166534]" onClick={() => navigate("/templates/new/edit")}>
            <Plus className="w-4 h-4 mr-2" />New Template
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
        {templates.map((t) => {
          const qCount = t.sections.reduce((n, s) => n + s.questions.length, 0);
          return (
            <div key={t.id} data-testid={`template-card-${t.id}`} className="bg-white border border-[#E7E5DF] rounded-xl p-6 hover:shadow-md transition-all hover:-translate-y-0.5">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-lg bg-[#D1FAE5] flex items-center justify-center">
                  <FileSpreadsheet className="w-5 h-5 text-[#14532D]" strokeWidth={1.5} />
                </div>
                <span className="text-[10px] font-mono text-stone-400">v{t.version}</span>
              </div>
              <h3 className="mt-4 font-bold text-stone-900 tracking-tight">{t.name}</h3>
              <p className="text-xs text-stone-500 mt-1 line-clamp-2 min-h-[32px]">{t.description || "No description"}</p>
              <div className="flex gap-4 mt-3 text-xs text-stone-500 font-mono">
                <span>{t.sections.length} sections</span>
                <span>{qCount} questions</span>
              </div>
              <div className="flex gap-2 mt-5 pt-4 border-t border-stone-100">
                <Button data-testid={`template-edit-${t.id}`} size="sm" variant="outline" className="flex-1" onClick={() => navigate(`/templates/${t.id}/edit`)}>
                  <Pencil className="w-3.5 h-3.5 mr-1.5" />Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => duplicate(t.id)} data-testid={`template-duplicate-${t.id}`}><Copy className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={() => remove(t.id)} data-testid={`template-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          );
        })}
        {templates.length === 0 && <p className="text-sm text-stone-400 col-span-3 text-center py-16">No templates yet — create one or import from Excel.</p>}
      </div>

      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) setPreview(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Import Audit from Excel</DialogTitle></DialogHeader>
          {!preview && (
            <div
              className="border-2 border-dashed border-stone-300 rounded-xl p-10 text-center cursor-pointer hover:border-[#14532D] hover:bg-emerald-50/30 transition-colors"
              onClick={() => fileRef.current?.click()}
              data-testid="excel-dropzone"
            >
              <Upload className="w-10 h-10 mx-auto text-stone-400" strokeWidth={1.2} />
              <p className="mt-3 text-sm font-medium text-stone-700">{uploading ? "Analyzing your audit format..." : "Click to choose an .xlsx or .pdf audit file"}</p>
              <p className="text-xs text-stone-400 mt-1">Sections, questions, points, guidelines and past results are detected automatically. Summary sheets are skipped.</p>
              <input ref={fileRef} data-testid="excel-file-input" type="file" accept=".xlsx,.xlsm,.pdf" className="hidden" onChange={(e) => uploadFile(e.target.files[0])} />
              {uploading && <div className="mt-4 w-6 h-6 mx-auto border-2 border-[#14532D] border-t-transparent rounded-full animate-spin" />}
            </div>
          )}
          {preview && (
            <div className="space-y-4" data-testid="import-preview">
              <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm">
                <CheckCircle2 className="w-4 h-4" />
                Detected <b>{preview.stats.sections} sections</b> and <b>{preview.stats.questions} questions</b> in "{preview.name}"
              </div>
              {preview.warnings.map((w, i) => (
                <div key={i} className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-xs">
                  <AlertTriangle className="w-4 h-4 shrink-0" />{w}
                </div>
              ))}
              <div className="max-h-72 overflow-y-auto border border-[#E7E5DF] rounded-lg divide-y divide-stone-100">
                {preview.sections.map((s, i) => (
                  <div key={i} className="px-4 py-3">
                    <div className="flex justify-between items-center">
                      <p className="text-sm font-semibold text-stone-800">{s.title}</p>
                      <span className="text-[10px] font-mono bg-stone-100 px-2 py-0.5 rounded-full text-stone-600">{s.department}</span>
                    </div>
                    <p className="text-xs text-stone-500 mt-0.5">{s.questions.length} questions · {s.questions.reduce((n, q) => n + q.weight, 0)} points</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => { setPreview(null); setHist({ enabled: false, hotel_id: "", audit_date: "", auditor_name: "" }); }}>Choose another file</Button>
                <Button data-testid="confirm-import-button" className="flex-1 bg-[#14532D] hover:bg-[#166534]" disabled={creating || (hist.enabled && (!hist.hotel_id || !hist.audit_date))} onClick={createFromPreview}>
                  {creating ? "Importing..." : hist.enabled ? "Import Template + Results" : "Create Template"}
                </Button>
              </div>
              {preview.has_results && (
                <div className="border border-[#E7E5DF] rounded-lg p-4 space-y-3" data-testid="historical-import-panel">
                  <label className="flex items-center gap-2 text-sm font-medium text-stone-800 cursor-pointer">
                    <input data-testid="historical-toggle" type="checkbox" className="accent-[#14532D] w-4 h-4" checked={hist.enabled} onChange={(e) => setHist({ ...hist, enabled: e.target.checked })} />
                    This file contains past audit results — also import them as a completed audit
                  </label>
                  {hist.enabled && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <select data-testid="historical-hotel-select" className="border border-stone-300 rounded-lg px-3 py-2 text-sm bg-white" value={hist.hotel_id} onChange={(e) => setHist({ ...hist, hotel_id: e.target.value })}>
                        <option value="">Hotel...</option>
                        {hotels.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                      </select>
                      <input data-testid="historical-date-input" type="date" className="border border-stone-300 rounded-lg px-3 py-2 text-sm bg-white" value={hist.audit_date} onChange={(e) => setHist({ ...hist, audit_date: e.target.value })} />
                      <input data-testid="historical-auditor-input" className="border border-stone-300 rounded-lg px-3 py-2 text-sm bg-white" placeholder="Auditor name" value={hist.auditor_name} onChange={(e) => setHist({ ...hist, auditor_name: e.target.value })} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
